import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAllRedemptionsThunk } from "../../../store/thunks/jewelleryPlan/jewelleryThunks";
import {
  SearchIcon,
  FilterIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from "@heroicons/react/outline";

const RedemptionList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { redemptions, loading } = useSelector(
    (state) => state.jewelleryPlan?.jewellery
  );

  const [filters, setFilters] = useState({
    status: "",
    search: "",
    dateFrom: "",
    dateTo: "",
    page: 1,
    limit: 20,
  });

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchRedemptions();
  }, [filters.page, filters.status]);

  const fetchRedemptions = async () => {
    await dispatch(getAllRedemptionsThunk(filters));
  };

  const handleSearch = () => {
    setFilters({ ...filters, page: 1 });
    fetchRedemptions();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { color: "warning", icon: ClockIcon, text: "Pending" },
      COMPLETED: { color: "success", icon: CheckCircleIcon, text: "Completed" },
      CANCELLED: { color: "danger", icon: XCircleIcon, text: "Cancelled" },
      PROCESSING: { color: "secondary", icon: ClockIcon, text: "Processing" },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-light text-${config.color}-dark`}
      >
        <Icon className="h-3 w-3 mr-1" />
        {config.text}
      </span>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text">Redemptions</h1>
        <p className="mt-1 text-sm text-neutral-dark">
          Manage all jewellery plan redemptions
        </p>
      </div>

      <div className="bg-background rounded-lg shadow mb-6">
        <div className="p-4 border-b border-neutral-light">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Search by redemption number, customer name, or plan number..."
                  className="w-full pl-10 pr-4 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                />
                <SearchIcon className="absolute left-3 top-2.5 h-5 w-5 text-neutral-dark" />
              </div>
            </div>

            <div className="flex gap-2">
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value, page: 1 })
                }
                className="px-4 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
              >
                <option value="">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="PROCESSING">Processing</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
              </select>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-4 py-2 border border-neutral-light rounded-lg hover:bg-neutral-light flex items-center"
              >
                <FilterIcon className="h-5 w-5 mr-2" />
                Filters
              </button>

              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-primary text-text-on-primary rounded-lg hover:bg-primary-dark"
              >
                Search
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-light">
            <thead className="bg-neutral-light">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Redemption #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Plan Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-neutral-light">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : redemptions?.list?.length > 0 ? (
                redemptions.list.map((redemption) => (
                  <tr key={redemption._id} className="hover:bg-neutral-light">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-text">
                        {redemption.redemptionNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-text">
                        {redemption.planId?.planNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-text">
                        {redemption.customerId?.name}
                      </div>
                      <div className="text-xs text-neutral-dark">
                        {redemption.customerId?.phoneNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-text">
                        {redemption.items?.length || 0} items
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-text">
                        {formatAmount(redemption.summary?.totalValue || 0)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(redemption.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-dark">
                      {formatDate(redemption.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() =>
                          navigate(`/jewellery/redemptions/${redemption._id}`)
                        }
                        className="text-primary hover:text-primary-dark"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="px-6 py-4 text-center text-neutral-dark"
                  >
                    No redemptions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RedemptionList;
